import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, ShieldCheckIcon, BoltIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

function AdminPaymentSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    verboseLogging: false,
    testMode: true,
    provider: 'iyzico'
  });

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  const fetchPaymentSettings = async () => {
    try {
      setLoading(true);
      const response = await api('/api/admin/payment-settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.success) {
        setSettings({
          verboseLogging: response.data.verboseLogging || false,
          testMode: response.data.testMode || true,
          provider: response.data.provider || 'iyzico'
        });
      }
    } catch (error) {
      console.error('Payment settings fetch error:', error);
      // Hata durumunda varsayılan değerleri kullan
      toast.info('Ayarlar yüklenemedi, varsayılan değerler kullanılıyor');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVerboseLogging = async () => {
    const newValue = !settings.verboseLogging;
    
    try {
      setSaving(true);
      const response = await api.post('/api/admin/payment-settings/verbose-logging', {
        enabled: newValue
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.success) {
        setSettings(prev => ({ ...prev, verboseLogging: newValue }));
        toast.success(
          newValue 
            ? '✅ Detaylı logging aktif edildi' 
            : '❌ Detaylı logging kapatıldı',
          { position: 'top-center' }
        );
      }
    } catch (error) {
      console.error('Verbose logging toggle error:', error);
      toast.error('Ayar değiştirilemedi: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Ayarlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl mb-6 shadow-2xl">
            <Cog6ToothIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Payment Ayarları</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ödeme sistemi ve debugging ayarlarını yönetin
          </p>
        </div>

        {/* Current Status Banner */}
        <div className={`mb-8 p-6 rounded-2xl shadow-lg ${settings.testMode ? 'bg-gradient-to-r from-amber-100 to-yellow-100 border-2 border-amber-300' : 'bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300'}`}>
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${settings.testMode ? 'bg-amber-500' : 'bg-green-500'}`}>
              {settings.testMode ? (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ) : (
                <CheckCircleIcon className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className={`text-xl font-bold ${settings.testMode ? 'text-amber-800' : 'text-green-800'}`}>
                {settings.testMode ? 'Test Modu Aktif' : 'Production Modu'}
              </h3>
              <p className={`text-sm ${settings.testMode ? 'text-amber-700' : 'text-green-700'}`}>
                {settings.testMode 
                  ? 'Ödeme sistemi şu anda Sandbox/Test modunda çalışıyor. Gerçek para işlemi yapılmıyor.' 
                  : 'Ödeme sistemi canlı modda. Gerçek para işlemleri yapılıyor.'}
              </p>
            </div>
          </div>
        </div>

        {/* Settings Cards */}
        <div className="space-y-6">
          {/* Verbose Logging Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-gray-100 hover:border-blue-200 transition-all duration-300">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                    <BoltIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Detaylı Payment Logging</h3>
                    <p className="text-blue-100 text-sm mt-1">Ödeme işlemlerinin detaylı loglanması</p>
                  </div>
                </div>
                
                {/* Toggle Switch */}
                <button
                  onClick={handleToggleVerboseLogging}
                  disabled={saving}
                  className={`relative inline-flex h-10 w-20 items-center rounded-full transition-all duration-300 ${
                    saving 
                      ? 'bg-gray-400 cursor-wait' 
                      : settings.verboseLogging 
                        ? 'bg-green-500' 
                        : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-8 w-8 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                      settings.verboseLogging ? 'translate-x-11' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="p-8">
              {/* Status */}
              <div className={`mb-6 p-4 rounded-xl border-2 ${
                settings.verboseLogging 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    settings.verboseLogging ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                  }`}></div>
                  <span className={`font-semibold ${
                    settings.verboseLogging ? 'text-green-700' : 'text-gray-600'
                  }`}>
                    {settings.verboseLogging ? 'Aktif - Tüm detaylar loglanıyor' : 'Kapalı - Sadece önemli loglar'}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Verbose Logging Aktif Olduğunda:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">✓</span>
                      <span>Tüm İyzico API request ve response'ları detaylı loglanır</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">✓</span>
                      <span>Payment callback verileri tam detaylı kaydedilir</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">✓</span>
                      <span>Database transaction adımları loglanır</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1">✓</span>
                      <span>Hata debug işlemleri kolaylaşır</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h5 className="font-bold text-amber-800 mb-1">⚠️ Önemli Not</h5>
                      <p className="text-sm text-amber-700">
                        Verbose logging performansı etkileyebilir ve log dosyası boyutunu artırır. 
                        Sadece debugging sırasında aktif tutun. Production ortamında sürekli açık tutmayın.
                      </p>
                    </div>
                  </div>
                </div>

                {settings.verboseLogging && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                    <h5 className="font-bold text-blue-800 mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Log Dosyası Konumu
                    </h5>
                    <code className="text-sm text-blue-700 bg-blue-100 px-3 py-1 rounded block">
                      /minela-debug.log
                    </code>
                    <p className="text-xs text-blue-600 mt-2">
                      Logları görüntülemek için sunucu dosya sistemine erişim gereklidir
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Provider Info Card */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-gray-100">
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                  <ShieldCheckIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Ödeme Sağlayıcı</h3>
                  <p className="text-purple-100 text-sm mt-1">Aktif payment gateway bilgileri</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <img 
                  src="/iyzico-logo.png" 
                  alt="İyzico"
                  className="h-10 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-10 h-10 bg-purple-100 rounded items-center justify-center">
                  <span className="font-bold text-purple-600">İyzico</span>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">İyzico Payment Gateway</h4>
                  <p className="text-sm text-gray-600">Türkiye'nin önde gelen ödeme altyapısı</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Mod</p>
                  <p className="font-bold text-gray-800">
                    {settings.testMode ? '🧪 Sandbox (Test)' : '🚀 Production (Canlı)'}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Provider</p>
                  <p className="font-bold text-gray-800 uppercase">{settings.provider}</p>
                </div>
              </div>

              {/* Merchant Info Notice */}
              <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-xl">
                <h4 className="font-bold text-blue-800 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Satıcı (Merchant) Bilgileri
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  İyzico API'de satıcı bilgileri backend <code className="bg-blue-100 px-2 py-0.5 rounded">.env</code> dosyasından alınır:
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>COMPANY_NAME</strong>: Minela Kids.</li>
                  <li>• <strong>COMPANY_EMAIL</strong>: info@minelakids.com</li>
                  <li>• <strong>COMPANY_PHONE</strong>: +905552609420</li>
                  <li>• <strong>COMPANY_ADDRESS</strong>: Osmangazi mah. Rasim sk. 6/1, Sancaktepe/İstanbul</li>
                  <li>• <strong>COMPANY_TAX_NUMBER</strong>: 17309496212</li>
                </ul>
                <p className="text-xs text-blue-600 mt-3">
                  Bu bilgiler İyzico ödeme formlarında ve faturalarda kullanılır. Değiştirmek için backend <code className="bg-blue-100 px-1 rounded">.env</code> dosyasını düzenleyin.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500 text-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Değişiklikler anında aktif olur. Sunucu yeniden başlatmaya gerek yoktur.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPaymentSettings;
